import { useRef, useState } from 'react';
import { FILE_KINDS, FILE_RULES, fileSizeText, pastorApi, errorMessage } from '../pastorApi';
import type { PastorFile } from '../pastorApi';

// 사역자 본인 — 자기소개서 등 구직 서류 올리기·내려받기·삭제 (비공개 보관, 동의한 교회에만 이력서와 함께 전달)
export default function PastorFiles({ files, onChange }: { files: PastorFile[]; onChange: (files: PastorFile[]) => void }) {
  const [kind, setKind] = useState(FILE_KINDS[0]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const upload = async (file: File | undefined) => {
    if (!file) return;
    const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    if (!FILE_RULES.extensions.includes(ext)) return alert(`${FILE_RULES.extensions.join(', ')} 파일만 올릴 수 있습니다.`);
    if (file.size > FILE_RULES.maxFileMb * 1024 * 1024) return alert(`파일은 ${FILE_RULES.maxFileMb}MB 이하여야 합니다.`);
    setBusy(true);
    try {
      onChange(await pastorApi.uploadFile(file, kind));
    } catch (e) {
      alert(errorMessage(e, '서류를 올리지 못했습니다.'));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = async (f: PastorFile) => {
    if (!window.confirm(`'${f.name}'을(를) 삭제할까요? 이미 이력서를 전달받은 교회도 더는 내려받을 수 없습니다.`)) return;
    try {
      await pastorApi.deleteFile(f.id);
      onChange(files.filter((x) => x.id !== f.id));
    } catch (e) {
      alert(errorMessage(e));
    }
  };

  const download = (f: PastorFile) => pastorApi.downloadFile(f).catch((e) => alert(errorMessage(e, '내려받지 못했습니다.')));

  return (
    <div className="pc-files">
      {files.length === 0 ? (
        <p className="pc-files__empty">아직 올린 서류가 없습니다.</p>
      ) : (
        <ul className="pc-files__list">
          {files.map((f) => (
            <li key={f.id}>
              <span className="pc-files__kind">{f.kind}</span>
              <button type="button" className="pc-files__name" onClick={() => download(f)} title="내려받기">{f.name}</button>
              <span className="pc-files__meta">{fileSizeText(f.size)} · {f.createdAt?.slice(0, 10)}</span>
              <button type="button" className="mm-btn--ghost pc-files__del" onClick={() => remove(f)}>삭제</button>
            </li>
          ))}
        </ul>
      )}
      {files.length < FILE_RULES.maxFiles && (
        <div className="pc-inline pc-files__add">
          <select className="mm-field" value={kind} onChange={(e) => setKind(e.target.value)} aria-label="서류 종류">
            {FILE_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <button type="button" className="mm-btn mm-btn--outline pc-btn-md" disabled={busy} onClick={() => inputRef.current?.click()}>
            {busy ? '올리는 중…' : '파일 선택해 올리기'}
          </button>
          <input ref={inputRef} type="file" hidden accept={FILE_RULES.extensions.join(',')} onChange={(e) => upload(e.target.files?.[0])} />
        </div>
      )}
      <p className="pc-hint">{FILE_RULES.extensions.map((x) => x.slice(1).toUpperCase()).join('·')} · 파일당 {FILE_RULES.maxFileMb}MB · 최대 {FILE_RULES.maxFiles}개</p>
    </div>
  );
}
